import React from 'react';
import { Link } from 'react-router-dom';

export default function PartnerDisclaimer() {
  return (
    <div className="bg-gray-50 min-h-screen">
      <section className="relative py-16 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Partner Disclaimer</h1>
            <p className="text-lg text-gray-300">
              Important information for travel operators, sponsors, and business partners.
            </p>
          </div>
        </div>
      </section>

      <div className="section-container">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Disclaimer Sections</h3>
                <nav className="space-y-2">
                  <a href="#overview" className="block text-gray-600 hover:text-primary">Partnership Overview</a>
                  <a href="#responsibilities" className="block text-gray-600 hover:text-primary">Partner Responsibilities</a>
                  <a href="#compliance" className="block text-gray-600 hover:text-primary">Compliance Requirements</a>
                  <a href="#brand" className="block text-gray-600 hover:text-primary">Brand Usage Guidelines</a>
                  <a href="#termination" className="block text-gray-600 hover:text-primary">Agreement Termination</a>
                  <a href="#confidentiality" className="block text-gray-600 hover:text-primary">Confidentiality</a>
                </nav>
              </div>
              
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                <h4 className="font-bold text-gray-900 mb-2">Important Notice</h4>
                <p className="text-sm text-gray-700">
                  This disclaimer outlines key partnership requirements. Formal agreements contain complete terms.
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 md:p-8">
                <div className="prose prose-lg max-w-none">
                  
                  <section id="overview" className="mb-10">
                    <div className="flex items-start mb-6">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                        <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">Partnership Overview</h2>
                        <p className="text-gray-600">Understanding the MGL Experiences partnership model</p>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <p className="text-gray-700">
                        MGL Experiences Ltd operates as an experience design and intellectual property company. Our partnerships are structured around licensing our experience IP to qualified travel operators and collaborating with brands and sponsors.
                      </p>
                      
                      <div className="bg-blue-50 p-6 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Partnership Principles</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="p-4 bg-white rounded-lg">
                            <h4 className="font-semibold text-gray-900 mb-2">IP Licensing Model</h4>
                            <p className="text-sm text-gray-600">MGL owns all experience IP; partners license execution rights</p>
                          </div>
                          <div className="p-4 bg-white rounded-lg">
                            <h4 className="font-semibold text-gray-900 mb-2">Quality Standards</h4>
                            <p className="text-sm text-gray-600">Partners must maintain our defined quality and service levels</p>
                          </div>
                          <div className="p-4 bg-white rounded-lg">
                            <h4 className="font-semibold text-gray-900 mb-2">Brand Compliance</h4>
                            <p className="text-sm text-gray-600">Strict adherence to brand guidelines and messaging</p>
                          </div>
                          <div className="p-4 bg-white rounded-lg">
                            <h4 className="font-semibold text-gray-900 mb-2">Performance Metrics</h4>
                            <p className="text-sm text-gray-600">Regular reporting and performance monitoring required</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section id="responsibilities" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Partner Responsibilities</h2>
                    
                    <div className="space-y-6">
                      <div className="border-l-4 border-primary pl-4">
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">Travel Operator Responsibilities</h3>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                            <span className="text-gray-700">Operational execution and guest management</span>
                          </li>
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                            <span className="text-gray-700">Compliance with local regulations and licensing</span>
                          </li>
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                            <span className="text-gray-700">Insurance coverage and risk management</span>
                          </li>
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                            <span className="text-gray-700">Quality assurance and guest satisfaction</span>
                          </li>
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                            <span className="text-gray-700">Financial management and reporting</span>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="border-l-4 border-secondary pl-4">
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">Sponsor/Brand Responsibilities</h3>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-secondary rounded-full mt-2 mr-3"></div>
                            <span className="text-gray-700">Timely provision of marketing assets and approvals</span>
                          </li>
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-secondary rounded-full mt-2 mr-3"></div>
                            <span className="text-gray-700">Compliance with sponsorship payment schedules</span>
                          </li>
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-secondary rounded-full mt-2 mr-3"></div>
                            <span className="text-gray-700">Alignment with brand safety and positioning guidelines</span>
                          </li>
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-secondary rounded-full mt-2 mr-3"></div>
                            <span className="text-gray-700">Cooperation in activation planning and execution</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  <section id="compliance" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Compliance Requirements</h2>
                    
                    <div className="bg-yellow-50 p-6 rounded-lg mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Mandatory Compliance Standards</h3>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Legal & Regulatory</h4>
                          <ul className="space-y-2 text-sm text-gray-700">
                            <li className="flex items-start">
                              <svg className="w-4 h-4 text-yellow-600 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Valid business registration
                            </li>
                            <li className="flex items-start">
                              <svg className="w-4 h-4 text-yellow-600 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Required travel industry licenses
                            </li>
                            <li className="flex items-start">
                              <svg className="w-4 h-4 text-yellow-600 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Insurance and liability coverage
                            </li>
                          </ul>
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Operational Standards</h4>
                          <ul className="space-y-2 text-sm text-gray-700">
                            <li className="flex items-start">
                              <svg className="w-4 h-4 text-yellow-600 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Quality management systems
                            </li>
                            <li className="flex items-start">
                              <svg className="w-4 h-4 text-yellow-600 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Staff training and certification
                            </li>
                            <li className="flex items-start">
                              <svg className="w-4 h-4 text-yellow-600 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Emergency response procedures
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section id="brand" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Brand Usage Guidelines</h2>
                    
                    <div className="space-y-6">
                      <div className="p-6 bg-gray-50 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Approved Usage</h3>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-700">Marketing approved MGL Experiences as a partner</span>
                          </li>
                          <li className="flex items-start">
                            <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-700">Using approved logos and brand assets</span>
                          </li>
                          <li className="flex items-start">
                            <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-700">Referencing partnership in approved communications</span>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="p-6 bg-red-50 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Prohibited Usage</h3>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-700">Claiming ownership of MGL Experience IP</span>
                          </li>
                          <li className="flex items-start">
                            <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-700">Modifying brand assets without approval</span>
                          </li>
                          <li className="flex items-start">
                            <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-700">Using brand assets after partnership termination</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  <section id="termination" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Agreement Termination</h2>
                    
                    <div className="space-y-4">
                      <div className="p-4 bg-gray-100 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Termination Rights</h4>
                        <p className="text-gray-700">
                          MGL Experiences Ltd reserves the right to terminate any partnership agreement for cause, including but not limited to breach of terms, failure to meet quality standards, or actions that damage our brand reputation.
                        </p>
                      </div>
                      
                      <div className="p-4 bg-gray-100 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Post-Termination Obligations</h4>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-gray-600 rounded-full mt-1.5 mr-2"></div>
                            Immediate cessation of brand asset usage
                          </li>
                          <li className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-gray-600 rounded-full mt-1.5 mr-2"></div>
                            Return of confidential information
                          </li>
                          <li className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-gray-600 rounded-full mt-1.5 mr-2"></div>
                            Fulfillment of existing guest commitments
                          </li>
                          <li className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-gray-600 rounded-full mt-1.5 mr-2"></div>
                            Settlement of outstanding financial obligations
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  <section id="confidentiality" className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Confidentiality</h2>
                    
                    <div className="bg-blue-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Confidential Information Protection</h3>
                      
                      <p className="text-gray-700 mb-4">
                        Partners agree to maintain the confidentiality of proprietary information shared during the partnership, including but not limited to:
                      </p>
                      
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div className="p-4 bg-white rounded-lg">
                          <h4 className="font-semibold text-gray-900 mb-2">Protected Information</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>Experience IP and designs</li>
                            <li>Pricing and financial terms</li>
                            <li>Business strategies</li>
                            <li>Partner lists</li>
                          </ul>
                        </div>
                        <div className="p-4 bg-white rounded-lg">
                          <h4 className="font-semibold text-gray-900 mb-2">Obligation Period</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>During partnership term</li>
                            <li>+ 3 years post-termination</li>
                            <li>Permanent for trade secrets</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </section>

                </div>
              </div>

              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">
                      For formal partnership agreements, please contact our partnerships team.
                    </p>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <Link
                      to="/legal-compliance"
                      className="text-sm text-primary font-medium hover:text-primary-dark"
                    >
                      ← Back to Legal & Compliance
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}